import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  X, Sparkles, Target, TrendingUp, Users, BarChart3,
  ArrowRight, Zap, Star, CheckCircle, Play
} from 'lucide-react';

const CRMIntroPopup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);

  // Don't show popup on CRM pages
  const isCRMPage = location.pathname.startsWith('/crm');

  const features = [
    { icon: Target, title: 'Smart Lead Capture', desc: 'AI-powered lead scoring' },
    { icon: TrendingUp, title: 'Visual Pipeline', desc: 'Drag & drop deals' },
    { icon: Users, title: 'Team Sync', desc: 'Real-time collaboration' },
    { icon: BarChart3, title: 'Live Analytics', desc: 'Instant insights' }
  ];

  useEffect(() => {
    // Don't show on CRM pages
    if (isCRMPage) return;

    const hasSeenCRMPopup = localStorage.getItem('crm_popup_seen');
    const lastShown = localStorage.getItem('crm_popup_last_shown');
    const now = Date.now();
    
    const shouldShow = !hasSeenCRMPopup || 
      (lastShown && (now - parseInt(lastShown)) > 24 * 60 * 60 * 1000);

    if (shouldShow) {
      const timer = setTimeout(() => {
        setIsVisible(true);
        localStorage.setItem('crm_popup_last_shown', now.toString());
      }, 10000);

      return () => clearTimeout(timer);
    }
  }, [isCRMPage]);

  // Rotate features
  useEffect(() => {
    if (!isVisible) return;
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % features.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [isVisible, features.length]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      localStorage.setItem('crm_popup_seen', 'true');
    }, 400);
  };

  const handleStartTrial = () => {
    localStorage.setItem('crm_popup_seen', 'true');
    navigate('/crm/checkout?plan=growth&trial=true');
  };

  const handleViewPricing = () => {
    localStorage.setItem('crm_popup_seen', 'true');
    navigate('/crm/pricing');
  };

  if (!isVisible || isCRMPage) return null;

  return (
    <div className={`fixed inset-0 z-[100] flex items-center justify-center p-4 transition-all duration-500 ${isClosing ? 'opacity-0' : 'opacity-100'}`}>
      {/* Animated Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-md"
        onClick={handleClose}
      />
      
      {/* Main Popup */}
      <div className={`relative w-full max-w-4xl transform transition-all duration-500 ${isClosing ? 'scale-90 opacity-0 rotate-2' : 'scale-100 opacity-100 rotate-0'}`}>
        <div className="relative rounded-3xl overflow-hidden">
          {/* Animated gradient border */}
          <div className="absolute -inset-[2px] bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-3xl animate-pulse opacity-75" />
          
          <div className="relative bg-[#0a0a1a] rounded-3xl overflow-hidden">
            {/* Animated background effects */}
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-full">
                <div className="absolute top-[-50%] left-[-20%] w-[600px] h-[600px] bg-blue-600/30 rounded-full blur-[120px] animate-pulse" />
                <div className="absolute bottom-[-30%] right-[-10%] w-[500px] h-[500px] bg-purple-600/30 rounded-full blur-[100px] animate-pulse" style={{animationDelay: '1s'}} />
                <div className="absolute top-[20%] right-[20%] w-[300px] h-[300px] bg-pink-500/20 rounded-full blur-[80px] animate-pulse" style={{animationDelay: '0.5s'}} />
              </div>
              {/* Grid pattern */}
              <div className="absolute inset-0 opacity-10" style={{
                backgroundImage: 'linear-gradient(rgba(255,255,255,.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.05) 1px, transparent 1px)',
                backgroundSize: '50px 50px'
              }} />
            </div>

            {/* Close button */}
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 z-20 p-2 text-white/50 hover:text-white hover:bg-white/10 rounded-full transition-all hover:rotate-90 duration-300"
              data-testid="crm-popup-close"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Content Grid */}
            <div className="relative z-10 grid md:grid-cols-2 gap-0">
              {/* Left Side - Visual */}
              <div className="p-8 md:p-12 flex flex-col justify-center items-center relative">
                {/* Floating badge */}
                <div className="absolute top-6 left-6 md:top-8 md:left-8">
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-full animate-bounce" style={{animationDuration: '2s'}}>
                    <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                    <span className="text-xs font-bold text-yellow-400">NEW LAUNCH</span>
                  </div>
                </div>

                {/* Main Visual - CRM Dashboard Preview */}
                <div className="relative w-full max-w-sm">
                  {/* Glowing orb behind */}
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl blur-2xl opacity-40 scale-90" />
                  
                  {/* Dashboard mockup */}
                  <div className="relative bg-slate-900/90 border border-slate-700/50 rounded-2xl p-4 backdrop-blur transform hover:scale-105 transition-transform duration-500">
                    {/* Mini header */}
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-3 h-3 rounded-full bg-red-500" />
                      <div className="w-3 h-3 rounded-full bg-yellow-500" />
                      <div className="w-3 h-3 rounded-full bg-green-500" />
                      <span className="ml-2 text-xs text-slate-500">CLOUD IT CRM</span>
                    </div>
                    
                    {/* Stats row */}
                    <div className="grid grid-cols-3 gap-2 mb-4">
                      {[
                        { value: '2.4K', label: 'Leads', color: 'from-blue-500 to-cyan-400' },
                        { value: '$847K', label: 'Pipeline', color: 'from-purple-500 to-pink-400' },
                        { value: '94%', label: 'Win Rate', color: 'from-green-500 to-emerald-400' }
                      ].map((stat, idx) => (
                        <div key={idx} className="bg-slate-800/50 rounded-lg p-2 text-center">
                          <div className={`text-lg font-bold bg-gradient-to-r ${stat.color} bg-clip-text text-transparent`}>
                            {stat.value}
                          </div>
                          <div className="text-[10px] text-slate-500">{stat.label}</div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Mini pipeline */}
                    <div className="flex gap-1 mb-3">
                      {['New', 'Contact', 'Proposal', 'Won'].map((stage, idx) => (
                        <div 
                          key={idx}
                          className={`flex-1 h-8 rounded-md flex items-center justify-center text-[9px] font-medium ${
                            idx === 0 ? 'bg-blue-500/20 text-blue-400' :
                            idx === 1 ? 'bg-yellow-500/20 text-yellow-400' :
                            idx === 2 ? 'bg-purple-500/20 text-purple-400' :
                            'bg-green-500/20 text-green-400'
                          }`}
                        >
                          {stage}
                        </div>
                      ))}
                    </div>
                    
                    {/* Activity indicator */}
                    <div className="flex items-center gap-2 text-xs text-slate-400">
                      <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                      <span>Live • 12 deals updated</span>
                    </div>
                  </div>
                </div>

                {/* Rotating features */}
                <div className="mt-6 h-16 flex items-center justify-center">
                  {features.map((feature, idx) => (
                    <div
                      key={idx}
                      className={`absolute flex items-center gap-3 px-4 py-2 bg-white/5 border border-white/10 rounded-full transition-all duration-500 ${
                        idx === activeFeature 
                          ? 'opacity-100 translate-y-0 scale-100' 
                          : 'opacity-0 translate-y-4 scale-90'
                      }`}
                    >
                      <feature.icon className="w-5 h-5 text-blue-400" />
                      <div>
                        <div className="text-sm font-medium text-white">{feature.title}</div>
                        <div className="text-xs text-slate-400">{feature.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right Side - Content */}
              <div className="p-8 md:p-12 flex flex-col justify-center bg-gradient-to-br from-slate-900/50 to-transparent">
                {/* Headline */}
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-2 leading-tight">
                  Meet Your New
                  <span className="block bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                    Sales Superpower
                  </span>
                </h2>
                
                <p className="text-slate-400 mb-6 text-sm md:text-base">
                  Close deals faster with AI-powered CRM. Join 500+ businesses already growing with us.
                </p>

                {/* Quick benefits */}
                <div className="space-y-3 mb-6">
                  {[
                    'Set up in 2 minutes, no training needed',
                    'Import your contacts with one click',
                    'See 40% more conversions on average'
                  ].map((benefit, idx) => (
                    <div key={idx} className="flex items-center gap-3 group">
                      <div className="w-6 h-6 rounded-full bg-gradient-to-r from-green-500 to-emerald-400 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-sm text-slate-300">{benefit}</span>
                    </div>
                  ))}
                </div>

                {/* Pricing highlight */}
                <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-xl p-4 mb-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                        <Zap className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <div className="text-white font-semibold">14 Days Free</div>
                        <div className="text-xs text-slate-400">No credit card required</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-white">$19<span className="text-sm font-normal text-slate-400">/mo</span></div>
                      <div className="text-xs text-green-400">Save 40% yearly</div>
                    </div>
                  </div>
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={handleStartTrial}
                    className="group flex-1 py-4 px-6 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 hover:from-blue-600 hover:via-purple-600 hover:to-pink-600 text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-all hover:scale-[1.02] hover:shadow-2xl hover:shadow-purple-500/30 relative overflow-hidden"
                    data-testid="crm-popup-trial"
                  >
                    <span className="relative z-10 flex items-center gap-2">
                      <Play className="w-5 h-5 fill-white" />
                      Start Free Trial
                    </span>
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                  </button>
                  
                  <button
                    onClick={handleViewPricing}
                    className="py-4 px-6 bg-white/5 hover:bg-white/10 text-white font-medium rounded-xl border border-white/10 hover:border-white/20 transition-all flex items-center justify-center gap-2"
                    data-testid="crm-popup-pricing"
                  >
                    See All Plans
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>

                {/* Social proof */}
                <div className="mt-6 flex items-center gap-4">
                  <div className="flex -space-x-2">
                    {[1,2,3,4].map((i) => (
                      <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-br from-slate-600 to-slate-800 border-2 border-[#0a0a1a] flex items-center justify-center text-xs font-bold text-white">
                        {String.fromCharCode(64 + i)}
                      </div>
                    ))}
                  </div>
                  <div className="text-xs text-slate-400">
                    <span className="text-white font-semibold">500+</span> companies growing with us
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CRMIntroPopup;
